import { useState, useEffect, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

type Props = {
  /** Stable id for localStorage: e.g. `${candidateId}-source` */
  storageKey: string;
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
};

export function CandidateCollapsiblePanel({ storageKey, title, defaultOpen = true, children, className = '' }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    try {
      const v = localStorage.getItem(`ggj.cand.panel.${storageKey}`);
      if (v === '0') setOpen(false);
      else if (v === '1') setOpen(true);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const toggle = () => {
    setOpen((o) => {
      const n = !o;
      try {
        localStorage.setItem(`ggj.cand.panel.${storageKey}`, n ? '1' : '0');
      } catch {
        /* ignore */
      }
      return n;
    });
  };

  return (
    <div
      className={`rounded-xl border border-[var(--vscode-border-input)] bg-[var(--vscode-bg-elevated)] shadow-[0_1px_0_0_color-mix(in_srgb,var(--vscode-border-input)_40%,transparent)] overflow-hidden ${className}`.trim()}
    >
      <button
        type="button"
        onClick={toggle}
        className="ggj-cand-rail-panel-header w-full flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors"
        aria-expanded={open}
      >
        <span className="ggj-cand-rail-panel-title">{title}</span>
        {open ? (
          <ChevronDown className="ggj-cand-rail-panel-chevron w-4 h-4 shrink-0 opacity-90" aria-hidden />
        ) : (
          <ChevronRight className="ggj-cand-rail-panel-chevron w-4 h-4 shrink-0 opacity-90" aria-hidden />
        )}
      </button>
      {open ? (
        <div className="px-4 pb-4 pt-3 border-t border-[var(--vscode-border-divider)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_55%,var(--vscode-bg-elevated))]">
          {children}
        </div>
      ) : null}
    </div>
  );
}
